/**
 * G/V-Helpers (S52).
 *
 * Backend liefert `pnl_abs`/`pnl_pct` in Positions-Währung und
 * `pnl_eur`/`cost_basis_eur` mit heutigem FX auf beiden Seiten
 * (reiner Kurs-G/V). Hier nur Formatierung + Aggregation.
 */

import { formatNumberDe } from '@/lib/format'
import type { Position } from '@/types/position'

const DASH = '—'

function toFinite(value: string | null): number | null {
  if (value === null || value.trim() === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** "+194,00 €" bzw. "−50,50 USD" — Vorzeichen explizit, non-EUR mit Code. */
export function formatPnlAmount(abs: string | null, currency: string): string {
  const absNum = toFinite(abs)
  if (absNum === null) return DASH
  const sign = absNum >= 0 ? '+' : '−'
  const unit = currency === 'EUR' ? '€' : currency
  return `${sign}${formatNumberDe(Math.abs(absNum))} ${unit}`
}

/** "+10,7 %" — deutsches Format (formatPct aus format.ts ist englisch). */
export function formatPnlPct(pct: string | null): string | null {
  const pctNum = toFinite(pct)
  if (pctNum === null) return null
  return `${pctNum >= 0 ? '+' : '−'}${formatNumberDe(Math.abs(pctNum) * 100, 1)} %`
}

/** "+194,00 € (+10,7 %)" — kombinierte Einzeiler-Variante. */
export function formatPnl(abs: string | null, pct: string | null, currency: string): string {
  const amount = formatPnlAmount(abs, currency)
  if (amount === DASH) return DASH
  const pctText = formatPnlPct(pct)
  return pctText === null ? amount : `${amount} (${pctText})`
}

/** Stück × aktueller Kurs in Positions-Währung; null ohne Kurs. */
export function positionValue(quantity: string, price: string | null): number | null {
  const qty = toFinite(quantity)
  const p = toFinite(price)
  if (qty === null || p === null) return null
  return qty * p
}

/** Farb-Ton für G/V-Werte; 0 und fehlende Werte sind neutral. */
export function pnlTone(abs: string | null): 'gain' | 'loss' | 'neutral' {
  const n = toFinite(abs)
  if (n === null || n === 0) return 'neutral'
  return n > 0 ? 'gain' : 'loss'
}

export interface PnlTotals {
  /** Summe pnl_eur über Positionen mit vollständigen Daten; null wenn keine. */
  pnlEur: number | null
  /** Summe position_value_eur (gleiche Positions-Teilmenge). */
  valueEur: number | null
  /** Gesamt-G/V relativ zum EUR-Einstand der vollständigen Positionen. */
  pct: number | null
  /** true wenn mindestens eine Position ohne pnl_eur ausgelassen wurde. */
  incomplete: boolean
}

/** Aggregiert G/V in EUR; Positionen ohne Kurs/FX werden ausgelassen. */
export function sumPnlEur(positions: Position[]): PnlTotals {
  let pnl = 0
  let cost = 0
  let value = 0
  let counted = 0
  let skipped = 0

  for (const pos of positions) {
    const pnlEur = toFinite(pos.pnl_eur)
    const costEur = toFinite(pos.cost_basis_eur)
    const valueEur = toFinite(pos.position_value_eur)
    if (pnlEur === null || costEur === null || valueEur === null) {
      skipped += 1
      continue
    }
    pnl += pnlEur
    cost += costEur
    value += valueEur
    counted += 1
  }

  if (counted === 0) {
    return { pnlEur: null, valueEur: null, pct: null, incomplete: skipped > 0 }
  }
  return {
    pnlEur: pnl,
    valueEur: value,
    pct: cost > 0 ? pnl / cost : null,
    incomplete: skipped > 0,
  }
}
